import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Inbox, ArrowLeft, LogOut, Lock, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { listEnquiries, adminLogin, adminMe } from "@/lib/api";
import { setToken, clearToken, getToken } from "@/lib/auth";
import { LOGO_URL } from "@/data/content";

const inputCls =
  "bg-[#0a0a0a] border-white/10 text-white h-12 rounded-lg focus-visible:ring-1 focus-visible:ring-zinc-400 focus-visible:border-zinc-500 placeholder:text-zinc-600";

function LoginCard({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const res = await adminLogin(email.trim(), password);
      setToken(res.access_token);
      toast.success("Signed in.");
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-5">
      <div className="pointer-events-none absolute inset-0 spotlight-top opacity-60" />
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#050505] p-8 sm:p-10"
      >
        <div className="mb-8 flex items-center gap-3">
          <img src={LOGO_URL} alt="Vision Studio" className="h-9 w-auto object-contain" />
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Vision Studio</span>
            <h1 className="font-display text-xl font-bold uppercase tracking-tight text-white">
              Studio Access
            </h1>
          </div>
        </div>

        <p className="mb-6 flex items-center gap-2 text-xs text-zinc-500">
          <Lock className="h-3.5 w-3.5" />
          Sign in to view enquiries.
        </p>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Email</Label>
            <Input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@thevisionstudio.in"
              className={inputCls}
              autoComplete="username"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Password</Label>
            <Input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputCls}
              autoComplete="current-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-zinc-200 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Signing in
            </>
          ) : (
            "Enter Studio"
          )}
        </button>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-white transition-colors"
          >
            ← Back to site
          </Link>
        </div>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listEnquiries();
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        setAuthed(false);
        toast.error("Session expired. Please sign in.");
      } else {
        toast.error("Failed to load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    try {
      await adminMe();
      setAuthed(true);
    } catch {
      clearToken();
      setAuthed(false);
    }
  };

  useEffect(() => {
    verify();
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const logout = () => {
    clearToken();
    setAuthed(false);
    setRows([]);
    toast.success("Signed out.");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500 text-xs uppercase tracking-[0.3em]">
        Checking session…
      </div>
    );
  }

  if (!authed) {
    return <LoginCard onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200 px-5 sm:px-8 lg:px-12 py-10">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between mb-10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Vision Studio" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-white">
                Enquiries
              </h1>
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                Admin · {rows.length} total
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-back"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-300 hover:border-white hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Site
            </Link>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-black hover:bg-zinc-200 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-300 hover:border-white hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#050505] overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-zinc-500">Loading enquiries…</div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-16 text-center text-zinc-500">
              <Inbox className="h-8 w-8" />
              No enquiries yet.
            </div>
          ) : (
            <Table data-testid="enquiries-table">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-zinc-500 uppercase tracking-widest text-[10px]">Name</TableHead>
                  <TableHead className="text-zinc-500 uppercase tracking-widest text-[10px]">Email</TableHead>
                  <TableHead className="text-zinc-500 uppercase tracking-widest text-[10px]">Company</TableHead>
                  <TableHead className="text-zinc-500 uppercase tracking-widest text-[10px]">Service</TableHead>
                  <TableHead className="text-zinc-500 uppercase tracking-widest text-[10px]">Message</TableHead>
                  <TableHead className="text-zinc-500 uppercase tracking-widest text-[10px]">Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.id}
                    data-testid="enquiry-row"
                    className="border-white/5 hover:bg-white/[0.03]"
                  >
                    <TableCell className="font-medium text-white">{r.name}</TableCell>
                    <TableCell className="text-zinc-400">{r.email}</TableCell>
                    <TableCell className="text-zinc-400">{r.company || "—"}</TableCell>
                    <TableCell className="text-zinc-400">{r.service || "—"}</TableCell>
                    <TableCell className="max-w-xs text-zinc-400">
                      <span className="line-clamp-2">{r.message}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-zinc-500">
                      {fmt(r.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
